import { BBABalance } from 'components/blockchain'
import { BlockNumber } from 'components/common/BlockNumber'
import { TxHash } from 'components/common/TxHash'
import Table from 'components/datatable/table'
import Moment from 'react-moment'
import BigNumber from 'bignumber.js'
import { TxInterface } from 'interfaces/tx.interface'
import { capitalCase } from 'text-case'

type Props = {
	pubKey: string
	transactions: TxInterface[]
}

type TransactionDirection = 'in' | 'out' | 'unknown'

function getTransactionDirection(
	transaction: TxInterface,
	accountPubkey: string
): { value: TransactionDirection; style: string } {
	const instruction = transaction.transaction.message.instructions.find(
		(ins) => ins.parsed?.type === 'transfer' && ins.program === 'system'
	)

	const outStyle = 'border-[#F3D65F] bg-[#FFF2DC] text-[#FFC107]'
	const inStyle = 'border-[#14AE00] bg-[#DEFFDC] text-[#14AE00]'

	if (!instruction) return { value: 'unknown', style: outStyle }

	const { source, destination } = instruction.parsed.info

	switch (accountPubkey) {
		case source:
			return { value: 'out', style: outStyle }
		case destination:
			return { value: 'in', style: inStyle }
		default:
			return { value: 'unknown', style: outStyle }
	}
}

const Transactions = ({ pubKey, transactions }: Props) => {
	const columns = [
		{
			title: 'Signature',
			render: (_value: any, tx: TxInterface) => <TxHash hash={tx.transaction.signatures[0]} link truncate={16} />
		},
		{
			title: 'Block',
			render: (_value: any, tx: TxInterface) => <BlockNumber block={tx.slot} link />
		},
		{
			title: 'Value',
			render: (_value: any, tx: TxInterface) => {
				const pre = new BigNumber(tx.meta.preBalances[0])
				const post = new BigNumber(tx.meta.postBalances[0])
				const value = pre.minus(post)
				return <BBABalance daltons={value.toNumber()} />
			}
		},
		{
			title: 'Fee',
			render: (_value: any, tx: TxInterface) => <BBABalance daltons={tx.meta.fee} />
		},
		{
			title: '',
			render: (_value: any, tx: TxInterface) => {
				const { value, style } = getTransactionDirection(tx, pubKey)
				return (
					<div
						className={`w-[31px] h-[22px] py-1 px-3 flex justify-center items-center  border rounded-[5px] text-xs ${style}`}
					>
						{capitalCase(value)}
					</div>
				)
			}
		},
		{
			title: 'Time',
			render: (_value: any, tx: TxInterface) => <Moment date={tx.blockTime * 1000} fromNow />
		},
		{
			title: 'Status',
			render: (_value: any, tx: TxInterface) => {
				let statusText
				let statusClass
				if (tx.meta?.err) {
					statusClass = 'warning'
					statusText = 'Failed'
				} else {
					statusClass = 'success'
					statusText = 'Success'
				}
				return <span className={`badge badge-${statusClass} badge-pill`}>{statusText}</span>
			}
		}
	]

	return (
		<div className="mb-12 mt-6 px-2.5 md:px-3 lg:px-5">
			<div className="box overflow-hidden bg-white dark:bg-darkPrimary">
				{/* TXS List */}
				<Table columns={columns} data={transactions} tableClassName="table w-full" />
			</div>
		</div>
	)
}

export default Transactions
